import {COMPANY_ID} from '@constants/auth.constant'
import {LANDING} from '@constants/internal-route.constant'
import {DashboardService} from '@services/api/dashboard.service'
import {LocalStorageService} from '@services/utils/localStorage.service'
import {createContext, ReactNode, useContext, useEffect, useState} from 'react'
import {Navigate} from 'react-router-dom'

/**
 * Mini-profile + direct-report count for the logged-in user, fetched once from `/dashboard/me`.
 * "Line manager" is not a role — it's derived here from `subordinateCount > 0` and used to gate the
 * team dashboard link/route. Mounted inside the authenticated tree (MasterLayout) so it only fetches
 * when a company is selected, and is shared by the menu + the route guard (no double fetch).
 */
export interface IMyContext {
  empId?: string | null
  code?: string
  name?: string
  designation?: string
  department?: string
  photo?: any
  subordinateCount?: number
  shiftInTime?: string
  shiftOutTime?: string
}

interface IMyContextValue {
  myEmp: IMyContext | null
  isLineManager: boolean
  loading: boolean
  refresh: () => void
}

const MyContext = createContext<IMyContextValue>({} as IMyContextValue)

const MyContextProvider = ({children}: {children: ReactNode}) => {
  const [myEmp, setMyEmp] = useState<IMyContext | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  const refresh = () => {
    // No company selected yet → nothing to resolve; don't leave the UI in a permanent loading state.
    if (!LocalStorageService.get(COMPANY_ID)) {
      setLoading(false)
      return
    }
    setLoading(true)
    DashboardService.getMyContext()
      .then((res) => setMyEmp(res?.body || null))
      .catch(() => setMyEmp(null))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    refresh()
    // eslint-disable-next-line
  }, [])

  const isLineManager = Number(myEmp?.subordinateCount || 0) > 0

  return (
    <MyContext.Provider value={{myEmp, isLineManager, loading, refresh}}>
      {children}
    </MyContext.Provider>
  )
}

const useMyContext = () => useContext(MyContext)

/**
 * Route guard for the line-manager dashboard: only employees with at least one direct report
  * may enter. Waits for the `/dashboard/me` fetch to resolve before deciding so a manager isn't
 * flash-redirected on refresh; non-managers redirect to the landing router.
 */
const RequireLineManager = ({children}: {children: ReactNode}) => {
  const {isLineManager, loading} = useMyContext()
  if (loading) return null
  return isLineManager ? <>{children}</> : <Navigate to={LANDING} replace />
}

export {MyContextProvider, useMyContext, RequireLineManager}
